import React, { useRef, useEffect } from 'react';
import './Features.css';

export default function Features() {
  const features = [
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M4 10h12a4 4 0 0 1 4 4v3a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-7Z" />
          <path d="M12 17h.01M16 17h.01M8 17h.01M6 10l2-4h6l2 4" />
          <path d="M4 14h16" />
        </svg>
      ),
      title: 'Left/Right Foot Analysis',
      desc: 'Unlock detailed parameters for each foot. Compare touches, release times, and kick power to cultivate balanced two-footed mastery.'
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="5" y="2" width="14" height="20" rx="2" />
          <line x1="12" y1="18" x2="12" y2="18.01" strokeWidth="2.5" />
          <path d="M9 6h6" strokeWidth="1.5"/>
        </svg>
      ),
      title: 'Seamless App Integration',
      desc: 'Sync performance data via Bluetooth immediately after training. Visualize your metrics, progression trends, and benchmark goals in a clean dashboard.'
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <line x1="12" y1="3" x2="12" y2="21" />
          <circle cx="12" cy="12" r="4" />
        </svg>
      ),
      title: 'Game & Practice Modes',
      desc: 'Switch between standard practices or intense match days. Capture speed, distance, sprints, work rate, and ball involvements dynamically.'
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
        </svg>
      ),
      title: 'Effective Motion Tracking',
      desc: 'Fitted with advanced 6-axis IMU sensors sampling at 1000 times per second to measure micro-movements, angle releases, and impact force.'
    }
  ];

  const cardRefs = useRef([]);

  useEffect(() => {
    const isMobile = () => window.matchMedia('(max-width: 768px)').matches;
    if (!isMobile()) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('scroll-active');
          } else {
            entry.target.classList.remove('scroll-active');
          }
        });
      },
      { threshold: 0.3, rootMargin: '0px 0px -60px 0px' }
    );

    const cards = cardRefs.current;
    cards.forEach((card) => { if (card) observer.observe(card); });

    const handleResize = () => {
      if (!isMobile()) {
        cards.forEach((card) => { if (card) card.classList.remove('scroll-active'); });
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cards.forEach((card) => { if (card) observer.unobserve(card); });
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <section className="features-section" id="features">
      <div className="container">
        <div className="section-header text-center">
          <span className="badge-volt">TECHNOLOGY BREAKTHROUGH</span>
          <h2 className="section-title">THE SCIENCE OF SOCCER TECHNOLOGY</h2>
          <p className="section-subtitle">
            Playermaker goes beyond standard GPS vests by mounting directly onto your cleats to analyze ball-to-foot interaction.
          </p>
        </div>

        <div className="features-grid">
          {features.map((feature, idx) => (
            <div
              key={idx}
              className="card-tech feature-card"
              ref={(el) => (cardRefs.current[idx] = el)}
            >
              <div className="feature-icon-wrapper flex-center">
                {feature.icon}
              </div>
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-desc">{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
